import {
  DateValidationError,
  DesktopDatePicker,
  PickerChangeHandlerContext,
} from '@mui/x-date-pickers'
import { usePartyDateFormat } from 'customHooks/usePartyDateTimeFormat'
import { isEqual } from 'lodash'
import moment, { Moment } from 'moment'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ShowException } from 'store/Application/appActions'
import { useAppDispatch, useAppSelector } from 'store/configureStore'
import { getDateFormatString } from 'utils/getDateFormatString'
import { formatMomentDateToSendToAPI } from '../../../utils/formatDateToSend'
import { saveProjectRequestedDeliveryDate } from '../store/asyncActions/saveProjectRequestedDeliveryDate'
import { projectSelectors } from '../store/selectors/projectSelectors'

export const ProjectRequestedDeliveryDate = () => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()
  const [validation, setValidation] = useState<string | undefined>(undefined)

  const isEditable = useAppSelector(
    projectSelectors.ProjectEditableStateSelector
  )

  const createdInPortal = useAppSelector(
    projectSelectors.projectCreatedInPortalSelector
  )

  const projectRequestedDeliveryDate = useAppSelector((state) => {
    const requestedDeliveryDate =
      projectSelectors.projectRequestedDeliveryDateSelector(state)

    return moment(requestedDeliveryDate, false)
  }, isEqual)

  const partyDateFormat = usePartyDateFormat()

  const projectDeliveryDays = useAppSelector(
    projectSelectors.projectDeliveryDaysSelector,
    isEqual
  )

  const projectEstimatedDeliveryDate = useAppSelector((state) => {
    return moment(projectSelectors.projectDeliveryDateSelector(state))
  }, isEqual)

  const minDate = useMemo(() => {
    if (projectDeliveryDays === null) {
      return moment().add(0, 'day')
    }

    return projectEstimatedDeliveryDate
  }, [projectDeliveryDays, projectEstimatedDeliveryDate])

  const handleSave = (
    value: Moment,
    context: PickerChangeHandlerContext<DateValidationError>
  ) => {
    try {
      if (context.validationError) {
        return
      }

      const dateToSave = formatMomentDateToSendToAPI(value)

      if (dateToSave) {
        dispatch(saveProjectRequestedDeliveryDate(dateToSave))
      }
    } catch (err) {
      ShowException('requested delivery date', err)
      console.error(err)
    }
  }

  const availableDeliveryDays = useAppSelector((state) => {
    return state.project.noDeliveryDates
  }, isEqual)

  const validateDate = (
    date: Moment
  ): 'past' | 'beforeEstimatedDeliveryDate' | 'notAllowedDate' | undefined => {
    if (!date) {
      return 'past'
    }

    if (date.isBefore(moment().add(-1, 'day'))) {
      return 'past'
    }

    if (date.isBefore(minDate)) {
      return 'beforeEstimatedDeliveryDate'
    }

    if (
      availableDeliveryDays &&
      availableDeliveryDays[date.format('YYYY-MM-DD')]
    ) {
      return 'notAllowedDate'
    }

    return undefined
  }

  return (
    <DesktopDatePicker
      label={t('project:requested-delivery-date', 'requested delivery date')}
      format={partyDateFormat}
      localeText={{
        fieldMonthPlaceholder: () => {
          return partyDateFormat.match(/(M+)/)?.[1]
        },
      }}
      value={projectRequestedDeliveryDate}
      onChange={(value, context) => {
        handleSave(value, context)
      }}
      disabled={!isEditable}
      slotProps={{
        textField: {
          required: createdInPortal,
          placeholder: partyDateFormat ?? getDateFormatString().toLowerCase(),
          helperText: validation,
          inputProps: {
            style: { textTransform: 'lowercase' },
            onDoubleClick: (e) => {
              e.stopPropagation()
            },
          },
        },
        openPickerButton: {
          id: 'requestedDeliveryDateBtn',
          onDoubleClick: (e) => e.stopPropagation(),
        },
      }}
      disablePast
      minDate={minDate}
      shouldDisableDate={(date) => {
        if (validateDate(date)) {
          return true
        }

        return false
      }}
      onError={(_, dateWithError) => {
        const dateValidation = validateDate(dateWithError)

        switch (dateValidation) {
          case 'past': {
            setValidation(t('common:invalid-date', 'invalid date'))
            break
          }
          case 'beforeEstimatedDeliveryDate': {
            setValidation(
              t(
                'project:requested-delivery-date-must-be-after-estimated-delivery-date',
                'minimum delivery date is {{date}}',
                {
                  date: minDate.format(
                    partyDateFormat ?? getDateFormatString()
                  ),
                }
              )
            )
            break
          }
          case 'notAllowedDate': {
            setValidation(
              t(
                'project:requested-delivery-date-not-available',
                'requested delivery date is not available'
              )
            )
            break
          }
          default: {
            setValidation(undefined)
          }
        }
      }}
    />
  )
}
